import React, {useCallback, useEffect, useMemo} from "react";
import {ComplianceManagerTab, DefaultState, Reducer} from "./state";
import ComplianceManagerUI from "./ui";
import {SET_DRAWER_VISIBILITY, SET_TAB} from "./constant";
import {useExtendedReducer} from "../../../hooks/custom-reducer-hook";
import {
    ComplianceCheckInPackage,
    ComplianceCheckInPackageFragmentFragment,
    ComplianceEngineResultFragmentFragment,
    ComplianceProviderResultFragmentFragment,
    ProviderType
} from "../../../__generated__/graphql/types";
import {usePaxNodeQuery, usePersonQuery} from "../graphql";
import {useAdHocComplianceCheck, useCheckPaxNodeCompliance} from "../compliance-checker/compliance-checker";
import {scanForProviders} from "../providers/provider-scanner";
import {PaxContext} from "../types";
import {useEventSubscription} from "context/event-context";

export type ComplianceManagerProps = (
    {
        paxNodeId: string;
    } | {
        checkInPackage: ComplianceCheckInPackage
}) & {
    defaultTab?: ComplianceManagerTab
}

/**
 * UI that displays detailed information about the compliance of a PaxNode.
 * Capable of taking compliance data from multiple sources (i.e. ISN and DataFlyt's internal compliance systems)
 * and compiling a single result which can be viewed by the user.
 *
 * Also allows the user to view results from individual sources for specific data pertaining to it.
 * @param props
 * @constructor
 */
const ComplianceManager: React.FC<ComplianceManagerProps> = (props) => {
    const api = useComplianceManagerApi(props);
    return <ComplianceManagerUI api={api} />
}

export default ComplianceManager;

function useComplianceManagerApi(props: ComplianceManagerProps){
    const [ state, dispatch ] = useExtendedReducer(Reducer, DefaultState);

    const hasPaxNode = ('paxNodeId' in props) && !!props.paxNodeId;

    const paxNodeData = usePaxNodeQuery(hasPaxNode ? props.paxNodeId : undefined, false, 5000);

    const personID = 'paxNodeId' in props ? paxNodeData.paxNode?.personID?._id : props.checkInPackage.fsPersonID;

    const personData = usePersonQuery(personID, false, 5000);

    const getPaxContext = useCallback(() => {
        if (paxNodeData.paxNode){
            const p =  paxNodeData.paxNode;
            return {
                paxNode: paxNodeData.paxNode,
                customerID: p.customerID?._id,
                employerID: p.employerID?._id,
                destinationID: p.destinationID?._id,
                icanID: p.personID?.icanID,
                veriflytID: p.personID?.veriflytID,
                isnID: p.personID?.credentials?.find((c) => c.type === 'ISN')?.key,
                firstName: p.firstName,
                lastName: p.lastName,
                personID: personID
            }
        }
        else if ('checkInPackage' in props){
            const p = props.checkInPackage;
            return {
                paxNode: null,
                customerID: p.fsCustomerID,
                employerID:p.fsEmployerID,
                destinationID: p.fsDestinationID,
                icanID: p.icanID,
                veriflytID: p.personVeriflytID,
                isnID: p.isnID,
                firstName: personData?.person?.firstName,
                lastName:personData?.person?.lastName,
                personID: personID
            }
        }
        return null;
    }, [ paxNodeData, personData, props ]);

    let paxContext: Omit<PaxContext, 'complianceResult'> = getPaxContext();

    const paxComplianceChecker = useCheckPaxNodeCompliance(hasPaxNode ? props.paxNodeId : undefined);
    const adhocCheckResult = useAdHocComplianceCheck('checkInPackage' in props ? props.checkInPackage : undefined);

    const complianceChecker = useMemo(() => {
        return {
            run: () => paxComplianceChecker.run(),
            loading: paxComplianceChecker?.loading || adhocCheckResult?.loading,
            error: paxComplianceChecker?.error || adhocCheckResult?.error,
            validationMessage: adhocCheckResult.validationMessage,
            result: paxComplianceChecker.result || adhocCheckResult.result,
        }
    }, [paxComplianceChecker, adhocCheckResult])

    useEventSubscription("veriflyt.entityRegisteredToVeriflyt", (data) => {
        if (data.entityID === personID){
            // Re-run compliance check when Veriflyt account is registered
            complianceChecker.run();
        }
    })

    useEffect(() => {
        if (props.defaultTab){
            dispatch({ type: SET_TAB, payload: props.defaultTab });
        }
    }, [props.defaultTab]);

    const paxNode = paxNodeData.paxNode;

    let complianceResult: ComplianceEngineResultFragmentFragment = null;

    if ('paxNodeId' in props){
        complianceResult = paxNode?.lastComplianceEngineResult || null;
    }
    else if ('checkInPackage' in props){
        complianceResult = adhocCheckResult.result;
    }

    const availableTabs = React.useMemo(
        () => findAvailableTabs(complianceResult),
        [ complianceResult ]
    );

    if (paxContext){
        (paxContext as PaxContext).complianceResult = complianceResult;
    }

    return {
        state: state,
        allowRunCheck: 'paxNodeId' in props ? !!props.paxNodeId : false,
        paxContext: paxContext as PaxContext,
        complianceResult: complianceResult,
        complianceChecker,
        availableTabs,
        setTab: (tab: ComplianceManagerTab) => dispatch({ type: SET_TAB, payload: tab }),
        setDrawerVisibility: (drawerType: keyof typeof state.drawers, visible: boolean) => {
            dispatch({
                type: SET_DRAWER_VISIBILITY,
                payload: {
                    drawerType,
                    visible
                }
            })
        },
        refetchPaxNode: () => paxNodeData.refetch()
    }
}

function findAvailableTabs(complianceEngineResult: ComplianceEngineResultFragmentFragment): ComplianceManagerTab[] {

    const availableTabs: ComplianceManagerTab[] = [
        ComplianceManagerTab.OVERALL
    ];

    if (!complianceEngineResult){ return availableTabs }

    const sourceToTab = (type: ProviderType) => {
        switch (type){
            case ProviderType.ISN:
                return ComplianceManagerTab.ISN;
            case ProviderType.VERIFLYT:
                return ComplianceManagerTab.VERIFLYT;
            case ProviderType.ICAN:
                return ComplianceManagerTab.ICAN;
        }
    }

    const results: ComplianceProviderResultFragmentFragment[] = scanForProviders(complianceEngineResult);

    results.forEach((providerResult) => {
        const tab = sourceToTab(providerResult.providerType);
        if (tab){
            availableTabs.push(tab);
        }
    });

    return availableTabs;
}

export type ComplianceManagerApi = ReturnType<typeof useComplianceManagerApi>
